import { Container } from "dioc"
import { isEqual } from "lodash-es"
import { computed } from "vue"
import { getDefaultRESTRequest } from "~/helpers/rest/default"
import { HoppRESTSaveContext, HoppTabDocument } from "~/helpers/rest/document"
import { getService } from "~/modules/dioc"
import { PersistenceService, STORE_KEYS } from "../persistence"
import { TabService } from "./tab"
import { PersistableTabState } from "."

export class RESTTabService extends TabService<HoppTabDocument> {
  public static readonly ID = "REST_TAB_SERVICE"

  constructor(c: Container) {
    super(c)

    this.tabMap.set("test", {
      id: "test",
      document: {
        type: "request",
        request: getDefaultRESTRequest(),
        isDirty: false,
        optionTabPreference: "params",
      },
    })

    this.watchCurrentTabID()
  }

  // override persistableTabState to remove response from the document
  public override persistableTabState = computed(() => ({
    lastActiveTabID: this.currentTabID.value,
    orderedDocs: this.tabOrdering.value.map((tabID) => {
      const tab = this.tabMap.get(tabID)! // tab ordering is guaranteed to have value for this key

      if (tab?.document?.type === "example-response") {
        return {
          tabID: tab?.id,
          doc: tab?.document,
        }
      }

      if (tab?.document?.type === "test-runner") {
        return {
          tabID: tab?.id,
          doc: {
            ...tab?.document,
            request: null,
            response: null,
          },
        }
      }

      return {
        tabID: tab?.id,
        doc: {
          ...tab?.document,
          response: null,
        },
      }
    }),
  }))

  protected async loadPersistedState(): Promise<PersistableTabState<HoppTabDocument> | null> {
    const persistenceService = getService(PersistenceService)
    const savedState = await persistenceService.getNullable<
      PersistableTabState<HoppTabDocument>
    >(STORE_KEYS.REST_TABS)
    return savedState
  }

  public getTabRefWithSaveContext(ctx: HoppRESTSaveContext) {
    for (const tab of this.tabMap.values()) {
      // For `team-collection` request id can be considered unique
      if (tab?.document?.type === "test-runner") continue

      if (ctx?.originLocation === "team-collection") {
        if (
          tab?.document?.saveContext?.originLocation === "team-collection" &&
          tab?.document?.saveContext?.requestID === ctx.requestID &&
          tab?.document?.saveContext?.exampleID === ctx.exampleID
        ) {
          return this.getTabRef(tab?.id)
        }
      } else if (isEqual(ctx, tab?.document?.saveContext)) {
        return this.getTabRef(tab?.id)
      }
    }

    return null
  }

  public getDirtyTabsCount() {
    let count = 0

    for (const tab of this.tabMap.values()) {
      if (tab?.document?.isDirty) count++
    }

    return count
  }
}
